import { create } from 'zustand';
import apiClient from '../lib/api';

export interface PurchaseOrder {
    id: string;
    tenantId: string;
    poNumber: string;
    vendorName: string;
    totalAmount: number;
    status: string;
    expectedDeliveryDate?: string;
    createdAt: string;
    updatedAt: string;
}

interface ProcurementState {
    orders: PurchaseOrder[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    fetchOrders: (filters?: any) => Promise<void>;
    createOrder: (data: Partial<PurchaseOrder>) => Promise<void>;
    updateOrder: (id: string, data: Partial<PurchaseOrder>) => Promise<void>;
}

export const useProcurementStore = create<ProcurementState>((set, get) => ({
    orders: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
    },
    fetchOrders: async (filters?: any) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/money/procurement', { params: filters });
            const { data, pagination } = response.data;
            set({ orders: data, pagination, isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch purchase orders', isLoading: false });
        }
    },
    createOrder: async (data: Partial<PurchaseOrder>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.post('/money/procurement', data);
            const newOrder = response.data;
            set((state) => ({
                orders: [newOrder, ...state.orders],
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to create purchase order', isLoading: false });
            throw error;
        }
    },
    updateOrder: async (id: string, data: Partial<PurchaseOrder>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.patch(`/money/procurement/${id}`, data);
            const updatedOrder = response.data;
            set((state) => ({
                orders: state.orders.map((o) => (o.id === id ? updatedOrder : o)),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to update purchase order', isLoading: false });
            throw error;
        }
    },
}));
